import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Download, Target, TrendingUp, Banknote, Gauge, Crosshair, AlertTriangle, CalendarClock, RefreshCw, TrendingDown, Users, User, FileText, CheckCircle, BarChart3, PieChart, Briefcase, UserCheck } from "lucide-react";

const norm = (r) => (r && typeof r === "object" && "data" in r ? r.data : r);

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortMonth = (k) => `${MONTH_SHORT[Number(k.slice(5, 7)) - 1]} ${String(Number(k.slice(0, 4))).slice(2)}`;
const TW = 860, TH = 230, PAD_L = 46, PAD_R = 14, PAD_T = 16, PAD_B = 36;
const DONUT_COLORS = ["#225f2b", "#0f766e", "#b45309", "#be123c", "#4c6a4f", "#6b5b3f", "#3f6b63", "#475569", "#7f1d1d", "#1e40af"];
const PERIODS = [
  { key: "all", label: "All" },
  { key: "30d", label: "30 days" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
];

const AdminPanelCrmDashboardPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [teamPerf, setTeamPerf] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [users, setUsers] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [contractsTab, setContractsTab] = useState("expired");
  const [contractsAlerts, setContractsAlerts] = useState({ expiring_soon: [], expired: [], renewed: [] });
  const [contractsExpired, setContractsExpired] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const loadAllLeads = async () => {
        const out = [];
        for (let pageNo = 1, guard = 0; guard < 25; guard++) {
          const res = await api.crm.list({ page: pageNo, limit: 1000, sort_by: "create_date", sort_dir: "asc" }).catch(() => null);
          if (!res) break;
          const arr = norm(res);
          const rows = Array.isArray(arr) ? arr : [];
          out.push(...rows);
          const total = typeof res?.total === "number" ? res.total : rows.length;
          if (rows.length === 0 || out.length >= total || rows.length < 1000) break;
          pageNo += 1;
        }
        return out;
      };
      const [f, tp, pl, st, us, lr, all] = await Promise.all([
        api.crm.forecast().catch(() => []),
        api.crm.teamPerformance().catch(() => []),
        api.crm.pipelineByStage().catch(() => []),
        api.crm.stages.list({ limit: 100 }).catch(() => []),
        api.users.list().catch(() => []),
        api.crm.lostReasons.list({ limit: 100 }).catch(() => []),
        loadAllLeads(),
      ]);
      setForecast(norm(f) || []);
      setTeamPerf(norm(tp) || []);
      setPipeline(norm(pl) || []);
      setStages(norm(st) || []);
      setUsers(norm(us) || []);
      setLostReasons(norm(lr) || []);
      setAllLeads(all);
      setLoading(false);

      const [alerts, expired] = await Promise.all([
        api.sales.contracts.alerts().catch(() => ({ data: { expiring_soon: [], expired: [], renewed: [] } })),
        api.sales.contracts.expired({ limit: 100 }).catch(() => ({ data: [] })),
      ]);
      setContractsAlerts(norm(alerts)?.data || { expiring_soon: [], expired: [], renewed: [] });
      setContractsExpired(norm(expired)?.data || []);
    })();
  }, []);

  const handleExport = async () => {
    try {
      await api.crm.export({});
      toast("Export downloaded", "success");
    } catch (e) {
      toast(e.message || "Export failed", "error");
    }
  };

  const maxForecast = useMemo(() => {
    let m = 0;
    (forecast || []).forEach((r) => {
      m = Math.max(m, Number(r.weighted_revenue) || 0, Number(r.expected_revenue) || 0);
    });
    return m || 1;
  }, [forecast]);

  const maxPipeline = useMemo(() => {
    let m = 0;
    (pipeline || []).forEach((r) => { m = Math.max(m, Number(r.expected_revenue) || 0); });
    return m || 1;
  }, [pipeline]);

  const stageMap = useMemo(() => new Map((stages || []).map((s) => [String(s.id), s])), [stages]);
  const stageOf = useCallback((l) => stageMap.get(String(l.stage_id)), [stageMap]);
  const isWon = useCallback((l) => !!stageOf(l)?.is_won, [stageOf]);
  const isLost = useCallback((l) => !!stageOf(l)?.is_lost, [stageOf]);

  const wonStageId = useMemo(() => (stages || []).find((s) => s.is_won)?.id, [stages]);
  const lostStageId = useMemo(() => (stages || []).find((s) => s.is_lost)?.id, [stages]);

  const periodCutoff = useMemo(() => {
    if (period === "all") return null;
    const now = new Date();
    if (period === "30d") { now.setDate(now.getDate() - 30); return now; }
    if (period === "quarter") return new Date(now.getFullYear(), now.getMonth() - 3, 1);
    if (period === "year") return new Date(now.getFullYear() - 1, now.getMonth(), 1);
    return null;
  }, [period]);

  const periodLeads = useMemo(() => {
    if (!periodCutoff) return allLeads || [];
    return (allLeads || []).filter((l) => {
      const d = new Date(l.date_open || l.created_at || "");
      return !isNaN(d) && d >= periodCutoff;
    });
  }, [allLeads, periodCutoff]);

  const stats = useMemo(() => {
    const list = periodLeads || [];
    const open = list.filter((l) => !isWon(l) && !isLost(l));
    const openOpps = open.filter((l) => l.type === "opportunity");
    const won = list.filter(isWon);
    const lost = list.filter(isLost);
    const wonRevenue = won.reduce((s, l) => s + (Number(l.won_revenue) || 0), 0);
    const lostValue = lost.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0);
    const closed = won.length + lost.length;
    return {
      total: list.length,
      open: openOpps.length,
      won: won.length,
      lost: lost.length,
      winRate: closed ? won.length / closed : 0,
      wonRevenue,
      lostValue,
      expected: openOpps.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0),
      pipeline: openOpps.reduce((s, l) => s + (Number(l.expected_revenue) || 0) * ((Number(l.probability) || 0) / 100), 0),
      avgWon: won.length ? wonRevenue / won.length : 0,
    };
  }, [periodLeads, isWon, isLost]);

  const trendMonths = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return arr;
  }, []);

  const trend = useMemo(() => {
    const map = {};
    trendMonths.forEach((k) => { map[k] = { created: 0, wonRev: 0 }; });
    (periodLeads || []).forEach((l) => {
      const k = String(l.date_open || l.created_at || "").slice(0, 7);
      if (!k || !map[k]) return;
      map[k].created += 1;
      if (isWon(l)) map[k].wonRev += Number(l.won_revenue) || 0;
    });
    return trendMonths.map((k) => ({ key: k, ...map[k] }));
  }, [periodLeads, trendMonths, isWon]);

  const maxWon = Math.max(1, ...trend.map((d) => d.wonRev));
  const maxCreated = Math.max(1, ...trend.map((d) => d.created));
  const lx = (i) => PAD_L + (trend.length <= 1 ? (TW - PAD_L - PAD_R) / 2 : (i * (TW - PAD_L - PAD_R)) / (trend.length - 1));
  const lyWon = (v) => PAD_T + (TH - PAD_T - PAD_B) * (1 - v / (maxWon * 1.15));
  const lyCreated = (v) => PAD_T + (TH - PAD_T - PAD_B) * (1 - v / (maxCreated * 1.15));
  const wonPoints = trend.map((d, i) => `${lx(i)},${lyWon(d.wonRev)}`).join(" ");
  const createdPoints = trend.map((d, i) => `${lx(i)},${lyCreated(d.created)}`).join(" ");

  const userOf = useCallback((id) => {
    if (id === null || id === undefined || id === "") return null;
    return (users || []).find((x) => String(x.id) === String(id)) || null;
  }, [users]);
  const userName = useCallback((id) => {
    const u = userOf(id);
    return u ? (u.employee_name || u.name || u.username || u.login || u.email) : "Unassigned";
  }, [userOf]);

  const ownerPerf = useMemo(() => {
    const map = new Map();
    (periodLeads || []).forEach((l) => {
      const key = l.user_id === null || l.user_id === undefined || l.user_id === "" ? "" : String(l.user_id);
      let row = map.get(key);
      if (!row) {
        row = { key, name: userName(key), leads: 0, open: 0, won: 0, lost: 0, wonRev: 0, pipeline: 0 };
        map.set(key, row);
      }
      row.leads += 1;
      if (isWon(l)) {
        row.won += 1;
        row.wonRev += Number(l.won_revenue) || 0;
      } else if (isLost(l)) {
        row.lost += 1;
      } else {
        row.open += 1;
        if (l.type === "opportunity") {
          row.pipeline += (Number(l.expected_revenue) || 0) * ((Number(l.probability) || 0) / 100);
        }
      }
    });
    return [...map.values()].sort((a, b) => b.pipeline - a.pipeline);
  }, [periodLeads, isWon, isLost, userName]);

  const sourceAnalysis = useMemo(() => {
    const map = new Map();
    (periodLeads || []).forEach((l) => {
      const key = l.source_name || "No source";
      let row = map.get(key);
      if (!row) { row = { key, leads: 0, open: 0, won: 0, exp: 0, wonRev: 0 }; map.set(key, row); }
      row.leads += 1;
      if (isWon(l)) {
        row.won += 1;
        row.wonRev += Number(l.won_revenue) || 0;
      } else if (!isLost(l)) {
        row.open += 1;
        row.exp += Number(l.expected_revenue) || 0;
      }
    });
    return [...map.values()].sort((a, b) => b.leads - a.leads);
  }, [periodLeads, isWon, isLost]);
  const maxSource = Math.max(1, ...sourceAnalysis.map((s) => s.leads));

  const reasonName = useCallback((id) => {
    if (id === null || id === undefined || id === "") return null;
    const r = (lostReasons || []).find((x) => String(x.id) === String(id));
    return r ? r.name : null;
  }, [lostReasons]);

  const lostReasonsStats = useMemo(() => {
    const map = new Map();
    (periodLeads || []).filter(isLost).forEach((l) => {
      const id = l.lost_reason_id === null || l.lost_reason_id === undefined || l.lost_reason_id === "" ? "" : String(l.lost_reason_id);
      const key = id ? (reasonName(id) || `#${id}`) : "No reason";
      let row = map.get(key);
      if (!row) { row = { key, count: 0, value: 0 }; map.set(key, row); }
      row.count += 1;
      row.value += Number(l.expected_revenue) || 0;
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [periodLeads, isLost, reasonName]);
  const lostTotal = lostReasonsStats.reduce((s, r) => s + r.count, 0);
  const donutSegments = useMemo(() => {
    const total = Math.max(1, lostTotal);
    let acc = 0;
    return lostReasonsStats.map((r, i) => {
      const pct = (r.count / total) * 100;
      const seg = { key: r.key, val: r.count, value: r.value, pct: Math.round(pct), color: DONUT_COLORS[i % DONUT_COLORS.length], offset: acc, len: pct };
      acc += pct;
      return seg;
    });
  }, [lostReasonsStats, lostTotal]);

  const topDeals = useMemo(() => {
    return (periodLeads || [])
      .filter((l) => !isWon(l) && !isLost(l) && l.type === "opportunity")
      .map((l) => ({ ...l, weighted: (Number(l.expected_revenue) || 0) * ((Number(l.probability) || 0) / 100) }))
      .sort((a, b) => b.weighted - a.weighted)
      .slice(0, 8);
  }, [periodLeads, isWon, isLost]);

  const openCrm = useCallback((params) => {
    navigate(`/admin-panel/crm${params ? `?${params}` : ""}`);
  }, [navigate]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="CRM Dashboard"
        subtitle="Pipeline KPIs, forecast and team performance"
        actions={
          <button onClick={handleExport} className="ess-btn ess-btn-secondary">
            <Download size={16} /> Export leads
          </button>
        }
      />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>Period</span>
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              background: period === p.key ? "#225f2b" : "#fff",
              color: period === p.key ? "#fff" : "#475569",
              border: period === p.key ? "1px solid #225f2b" : "1px solid #e2e7e2",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Total Leads" value={String(stats.total)} icon={Target} trend="All pipeline" onClick={() => openCrm("view=list")} />
        <StatCard label="Open Opportunities" value={String(stats.open)} icon={TrendingUp} trend="Not won / lost" onClick={() => openCrm("view=list")} />
        <StatCard label="Pipeline Value" value={fmt.moneyShort(stats.pipeline)} icon={Banknote} trend={fmt.money(stats.pipeline)} onClick={() => openCrm("view=list")} />
        <StatCard label="Expected Revenue" value={fmt.moneyShort(stats.expected)} icon={Crosshair} trend="Open, unweighted" onClick={() => openCrm("view=list")} />
      </div>
      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Won Revenue" value={stats.wonRevenue ? fmt.moneyShort(stats.wonRevenue) : "—"} icon={Banknote} trend={stats.wonRevenue ? fmt.money(stats.wonRevenue) : "Amount not recorded"} onClick={() => (wonStageId ? openCrm(`view=list&stage_id=${wonStageId}`) : openCrm("view=list"))} />
        <StatCard label="Avg Won Deal" value={stats.avgWon ? fmt.moneyShort(stats.avgWon) : "—"} icon={Gauge} trend={stats.won ? `${stats.won} deals won` : "No won deals yet"} />
        <StatCard label="Lost Value" value={fmt.moneyShort(stats.lostValue)} icon={TrendingUp} trend={stats.lost ? `${stats.lost} lost` : "No lost deals"} onClick={() => (lostStageId ? openCrm(`view=list&stage_id=${lostStageId}`) : openCrm("view=list"))} />
        <StatCard label="Win Rate" value={`${Math.round(stats.winRate * 100)}%`} icon={Gauge} trend={`${stats.won} won / ${stats.lost} lost`} onClick={() => openCrm("view=list")} />
      </div>

      <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", padding: 18, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Monthly trend</h3>
            <p style={{ fontSize: 12, color: "#64748b" }}>Leads created vs won revenue — last 12 months</p>
          </div>
        </div>
        {trend.every((d) => d.created === 0 && d.wonRev === 0) ? (
          <EmptyState icon={BarChart3} title="No trend data" subtitle="Create leads to see the monthly evolution" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <svg viewBox={`0 0 ${TW} ${TH}`} style={{ width: "100%", height: "auto", maxWidth: 900, minWidth: 560 }}>
              {Array.from({ length: 5 }, (_, i) => {
                const v = (maxWon * 1.15 / 4) * i;
                const yy = lyWon(v);
                return (
                  <g key={i}>
                    <line x1={PAD_L} y1={yy} x2={TW - PAD_R} y2={yy} stroke="#eef2f7" strokeWidth="1" />
                    <text x={PAD_L - 6} y={yy + 3.5} textAnchor="end" fontSize="10" fill="#94a3b8">{i === 0 ? "0" : fmt.moneyShort(v)}</text>
                  </g>
                );
              })}
              <polyline points={createdPoints} fill="none" stroke="#0f766e" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              <polyline points={wonPoints} fill="none" stroke="#225f2b" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              {trend.map((d, i) => (
                <g key={d.key}>
                  <circle cx={lx(i)} cy={lyCreated(d.created)} r="3.5" fill="#0f766e" stroke="#fff" strokeWidth="1.5" />
                  <circle cx={lx(i)} cy={lyWon(d.wonRev)} r="4" fill="#225f2b" stroke="#fff" strokeWidth="1.5">
                    <title>{`${shortMonth(d.key)} · ${d.created} lead${d.created > 1 ? "s" : ""} created · ${fmt.money(d.wonRev)} won`}</title>
                  </circle>
                  <text x={lx(i)} y={TH - PAD_B + 16} textAnchor="middle" fontSize="10" fill="#64748b">{shortMonth(d.key)}</text>
                </g>
              ))}
            </svg>
          </div>
        )}
        <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 12, color: "#64748b", flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, background: "#0f766e", borderRadius: 3, display: "inline-block" }} /> Leads created (normalized)</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, background: "#225f2b", borderRadius: 3, display: "inline-block" }} /> Won revenue (normalized)</span>
        </div>
      </div>

      <div style={{ display: "grid", gap: 24 }} className="ess-rg-2" >
        {/* Forecast */}
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Monthly forecast</h3>
              <p style={{ fontSize: 12, color: "#64748b" }}>Expected vs weighted revenue by deadline month</p>
            </div>
          </div>
          {forecast.length === 0 ? (
            <EmptyState icon={BarChart3} title="No forecast data" subtitle="Create open opportunities with deadlines" />
          ) : (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height: 200, overflowX: "auto", paddingBottom: 6 }}>
              {forecast.map((m) => (
                <div key={m.month} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 70 }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 150 }}>
                    <div
                      title={`Expected: ${fmt.money(m.expected_revenue)}`}
                      style={{ width: 16, height: `${Math.max(3, (Number(m.expected_revenue) || 0) / maxForecast * 150)}px`, background: "#225f2b", borderRadius: "4px 4px 0 0" }}
                    />
                    <div
                      title={`Weighted: ${fmt.money(m.weighted_revenue)}`}
                      style={{ width: 16, height: `${Math.max(3, (Number(m.weighted_revenue) || 0) / maxForecast * 150)}px`, background: "#6ee7b7", borderRadius: "4px 4px 0 0" }}
                    />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>{m.month}</span>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>{m.opportunities} opp.</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 12, color: "#64748b" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, background: "#225f2b", borderRadius: 3, display: "inline-block" }} /> Expected</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, background: "#6ee7b7", borderRadius: 3, display: "inline-block" }} /> Weighted (× proba)</span>
          </div>
        </div>

        {/* Pipeline by stage */}
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", padding: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b", marginBottom: 16 }}>Pipeline by stage</h3>
          {pipeline.length === 0 ? (
            <EmptyState icon={Target} title="No pipeline data" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {pipeline.map((s) => {
                const term = s.is_won || s.is_lost;
                const width = Math.max(4, (Number(s.expected_revenue) || 0) / maxPipeline * 100);
                return (
                  <div key={s.stage_id} onClick={() => openCrm(`view=list&stage_id=${s.stage_id}`)} title="Open stage in CRM" style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: term ? (s.is_won ? "#225f2b" : "#be123c") : "#334155" }}>
                        {s.stage_name} {term && <span style={{ fontSize: 10, fontWeight: 700 }}>({s.is_won ? "WON" : "LOST"})</span>}
                      </span>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{s.lead_count} leads · {fmt.money(s.expected_revenue)}</span>
                    </div>
                    <div style={{ height: 18, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${width}%`, height: "100%", background: term ? (s.is_won ? "#225f2b" : "#be123c") : "#225f2b", borderRadius: 4, opacity: term ? 0.85 : 1 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gap: 24 }} className="ess-rg-2">
        {/* Source analysis */}
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", padding: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>Leads by source</h3>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Volume and outcome per acquisition channel</p>
          {sourceAnalysis.length === 0 ? (
            <EmptyState icon={Target} title="No source data" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sourceAnalysis.map((s) => {
                const w = Math.max(4, (s.leads / maxSource) * 100);
                return (
                  <div key={s.key}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.key}</span>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{s.leads} leads · {s.won} won</span>
                    </div>
                    <div style={{ height: 18, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${w}%`, height: "100%", background: "#0f766e", borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lost reasons */}
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", padding: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>Lost reasons</h3>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Why deals were lost</p>
          {lostTotal === 0 ? (
            <EmptyState icon={AlertTriangle} title="No lost deals" subtitle="No lost leads recorded yet" />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", justifyContent: "center", padding: "6px 0" }}>
              <div style={{ position: "relative", width: 170, height: 170, flexShrink: 0 }}>
                <svg viewBox="0 0 120 120" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="20" />
                  {donutSegments.map((s, i) => (
                    <circle key={i} cx="60" cy="60" r="50" fill="none" stroke={s.color} strokeWidth="20" strokeDasharray={`${s.len} ${100 - s.len}`} strokeDashoffset={-s.offset} strokeLinecap="butt" />
                  ))}
                </svg>
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{lostTotal}</div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>lost</div>
                </div>
              </div>
              <div style={{ flex: "1 1 220px", minWidth: 200, maxWidth: 420 }}>
                {donutSegments.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12.5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                    <span style={{ color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.key}</span>
                    <span style={{ marginLeft: "auto", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>{s.val}</span>
                    <span style={{ color: "#94a3b8", width: 40, textAlign: "right", whiteSpace: "nowrap" }}>{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top deals */}
      <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", marginTop: 24, overflow: "hidden" }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e7e2" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Top deals</h3>
          <p style={{ fontSize: 12, color: "#64748b" }}>Largest open opportunities by weighted value — click a row to open in CRM</p>
        </div>
        {topDeals.length === 0 ? (
          <EmptyState icon={Briefcase} title="No open deals" subtitle="Open opportunities will appear here" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: 180 }}>Deal</th>
                  <th className="ess-th" style={{ minWidth: 130 }}>Owner</th>
                  <th className="ess-th" style={{ minWidth: 120 }}>Stage</th>
                  <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Proba</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Expected</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Weighted</th>
                </tr>
              </thead>
              <tbody>
                {topDeals.map((d, i) => (
                  <tr key={d.id} className="ess-tr" onClick={() => openCrm(`view=list&q=${encodeURIComponent(d.name)}`)} title={`Open "${d.name}" in CRM`} style={{ cursor: "pointer", borderBottom: i < topDeals.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{d.name}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{userName(d.user_id)}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{stageOf(d)?.name || "—"}</td>
                    <td className="ess-td" style={{ textAlign: "right" }}>{Number(d.probability) || 0}%</td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(d.expected_revenue)}</td>
                    <td className="ess-td" style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: "#225f2b" }}>{fmt.money(d.weighted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Team performance */}
      <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", marginTop: 24, overflow: "hidden" }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e7e2" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Team performance</h3>
          <p style={{ fontSize: 12, color: "#64748b" }}>Quotas, pipeline and conversion per sales team</p>
        </div>
        {teamPerf.length === 0 ? (
            <EmptyState icon={Users} title="No team data" subtitle="Assign leads to teams to see performance" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: 150 }}>Team</th>
                  <th className="ess-th" style={{ minWidth: 120, textAlign: "right" }}>Target</th>
                  <th className="ess-th" style={{ minWidth: 90, textAlign: "right" }}>Leads</th>
                  <th className="ess-th" style={{ minWidth: 110, textAlign: "right" }}>Opps</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Pipeline</th>
                  <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Won</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Won revenue</th>
                  <th className="ess-th" style={{ minWidth: 90, textAlign: "right" }}>Conversion</th>
                </tr>
              </thead>
              <tbody>
                {teamPerf.map((t, i) => (
                  <tr key={t.team_id} className="ess-tr" style={{ borderBottom: i < teamPerf.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{t.team_name || `#${t.team_id}`}</td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(t.target_revenue)}</td>
                    <td className="ess-td" style={{ textAlign: "right" }}>{t.total_leads ?? 0}</td>
                    <td className="ess-td" style={{ textAlign: "right" }}>{t.opportunities ?? 0}</td>
                    <td className="ess-td" style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{fmt.money(t.pipeline)}</td>
                    <td className="ess-td" style={{ textAlign: "right" }}>{t.won ?? 0}</td>
                    <td className="ess-td" style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{fmt.money(t.won_revenue)}</td>
                    <td className="ess-td" style={{ textAlign: "right" }}>{t.conversion != null ? `${Math.round(t.conversion * 100)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Owner performance */}
      <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", marginTop: 24, overflow: "hidden" }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e7e2" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Owner performance</h3>
          <p style={{ fontSize: 12, color: "#64748b" }}>Pipeline and conversion per salesperson</p>
        </div>
        {ownerPerf.length === 0 ? (
            <EmptyState icon={User} title="No owner data" subtitle="Assign leads to salespeople to see performance" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: 160 }}>Owner</th>
                  <th className="ess-th" style={{ minWidth: 90, textAlign: "right" }}>Leads</th>
                  <th className="ess-th" style={{ minWidth: 90, textAlign: "right" }}>Open</th>
                  <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Won</th>
                  <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Lost</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Pipeline</th>
                  <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Won revenue</th>
                  <th className="ess-th" style={{ minWidth: 90, textAlign: "right" }}>Conversion</th>
                </tr>
              </thead>
              <tbody>
                {ownerPerf.map((o, i) => {
                  const closed = o.won + o.lost;
                  const conv = closed ? (o.won / closed) * 100 : 0;
                  return (
                    <tr key={o.key} className="ess-tr" onClick={() => openCrm(o.key ? `view=list&user_id=${encodeURIComponent(o.key)}` : "view=list")} title="Open owner leads in CRM" style={{ cursor: "pointer", borderBottom: i < ownerPerf.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{o.name}</td>
                      <td className="ess-td" style={{ textAlign: "right" }}>{o.leads}</td>
                      <td className="ess-td" style={{ textAlign: "right" }}>{o.open}</td>
                      <td className="ess-td" style={{ textAlign: "right" }}>{o.won}</td>
                      <td className="ess-td" style={{ textAlign: "right" }}>{o.lost}</td>
                      <td className="ess-td" style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{fmt.money(o.pipeline)}</td>
                      <td className="ess-td" style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{fmt.money(o.wonRev)}</td>
                      <td className="ess-td" style={{ textAlign: "right" }}>{conv ? `${Math.round(conv)}%` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contracts */}
      <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", marginTop: 24, overflow: "hidden" }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e7e2", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Contracts</h3>
            <p style={{ fontSize: 12, color: "#64748b" }}>Expired, expiring soon and renewed sales orders</p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { key: "expired", label: "Expired" },
              { key: "alerts", label: "Alerts" },
              { key: "renewed", label: "Renewed" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setContractsTab(t.key)}
                style={{
                  padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer", border: "1px solid " + (contractsTab === t.key ? "#225f2b" : "#cbd5e1"),
                  background: contractsTab === t.key ? "#225f2b" : "#fff", color: contractsTab === t.key ? "#fff" : "#475569",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {contractsTab === "expired" && (
          contractsExpired.length === 0 ? (
            <EmptyState icon={FileText} title="No expired contracts" subtitle="Expired sales orders will appear here" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                    <th className="ess-th" style={{ minWidth: 180 }}>Order</th>
                    <th className="ess-th" style={{ minWidth: 160 }}>Customer</th>
                    <th className="ess-th" style={{ minWidth: 120 }}>Valid until</th>
                    <th className="ess-th" style={{ minWidth: 120, textAlign: "right" }}>Amount</th>
                    <th className="ess-th" style={{ minWidth: 120, textAlign: "right" }}>Expired days</th>
                  </tr>
                </thead>
                <tbody>
                  {contractsExpired.map((r, i) => (
                    <tr key={r.id} className="ess-tr" style={{ borderBottom: i < contractsExpired.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{r.name}</td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{r.partner_name || "—"}</td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{r.valid_until || "—"}</td>
                      <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.amount_total)}</td>
                      <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap", color: "#be123c", fontWeight: 600 }}>{r.expired_days ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {contractsTab === "alerts" && (
          <div style={{ padding: 14, display: "grid", gap: 14 }}>
            {["expiring_soon", "expired", "renewed"].map((group) => {
              const items = contractsAlerts[group] || [];
              const title = group === "expiring_soon" ? "Expiring soon (30 days)" : group === "expired" ? "Expired" : "Renewed (last 30 days)";
              const color = group === "expiring_soon" ? "#b45309" : group === "expired" ? "#be123c" : "#225f2b";
              return (
                <div key={group} style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>{title} · {items.length}</div>
                  {items.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>None</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {items.slice(0, 20).map((r) => (
                        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", background: "#fff", border: "1px solid #f1f3f0", borderRadius: 6 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{r.name}</div>
                            <div style={{ fontSize: 11, color: "#64748b" }}>{r.partner_name || "—"} · valid until {r.valid_until || "—"}</div>
                          </div>
                          <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{fmt.money(r.amount_total)}</div>
                            {r.renewal_reminder_date && <div style={{ fontSize: 10, color: "#94a3b8" }}>reminder {r.renewal_reminder_date}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {contractsTab === "renewed" && (
          contractsAlerts.renewed.length === 0 ? (
            <EmptyState icon={CheckCircle} title="No renewed contracts" subtitle="Renewed sales orders will appear here" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                    <th className="ess-th" style={{ minWidth: 180 }}>Order</th>
                    <th className="ess-th" style={{ minWidth: 160 }}>Customer</th>
                    <th className="ess-th" style={{ minWidth: 120 }}>Valid until</th>
                    <th className="ess-th" style={{ minWidth: 120, textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {contractsAlerts.renewed.map((r, i) => (
                    <tr key={r.id} className="ess-tr" style={{ borderBottom: i < contractsAlerts.renewed.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{r.name}</td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{r.partner_name || "—"}</td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{r.valid_until || "—"}</td>
                      <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.amount_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdminPanelCrmDashboardPage;